import cn from 'classnames';

import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import {InlineNotification} from '@/components/ui/InlineNotification';
import {PENDING_RETURN_ACTION_IDS} from '@/consts/pendingReturnActionIds';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import usePendingReturnActionRedirect from '@/hooks/usePendingReturnActionRedirect';
import {
    AUTO_DELIVERY_INFO_BOX_SOURCES,
    type AutoDeliveryInfoBoxData,
    type AutoDeliveryInfoBoxSource,
} from '@/types/content';
import {twx} from '@/utils/tailwindUtils';

import AutoDeliveryDcxInfoBoxActionButton from './AutoDeliveryDcxInfoBoxActionButton';

type AutoDeliveryDcxInfoBoxProps = {
    data: AutoDeliveryInfoBoxData | undefined;
    className?: string;
    source?: AutoDeliveryInfoBoxSource;
};

export const AutoDeliveryDcxInfoBox = ({data, className, source}: AutoDeliveryDcxInfoBoxProps) => {
    const {redirectToExternalLink} = useConfigExternalLink({...data?.notification?.actionButton?.externalLink});
    const redirectWithPendingAction = usePendingReturnActionRedirect(
        PENDING_RETURN_ACTION_IDS.AUTO_DELIVERY_DETAILS_EXTENDED,
        redirectToExternalLink
    );

    if (!data) return null;

    const {notification} = data;

    if (!notification) return null;

    const {title, description, icon, actionButton} = notification;

    const handleClick = () => {
        redirectWithPendingAction();
    };

    const isDashboard = source === AUTO_DELIVERY_INFO_BOX_SOURCES.DASHBOARD;

    return (
        <InlineNotification
            className={twx(className, isDashboard && 'md:flex-row md:items-center md:justify-between')}
            title={
                title ? (
                    <span className='inline-block text-16 leading-15' dangerouslySetInnerHTML={{__html: title}} />
                ) : undefined
            }
            body={
                description ? (
                    <span
                        className='inline-block text-14 leading-[20px]'
                        dangerouslySetInnerHTML={{__html: description}}
                    />
                ) : undefined
            }
            icon={icon ? <RemoteIconV2 className='size-12' icon={icon} /> : undefined}
            iconWrapperClassName='p-0'
        >
            {actionButton && (
                <AutoDeliveryDcxInfoBoxActionButton
                    label={actionButton.text}
                    alt={actionButton.alt}
                    icon={actionButton.icon ? <RemoteIconV2 className='size-6' icon={actionButton.icon} /> : undefined}
                    onClick={handleClick}
                    className={cn(
                        isDashboard &&
                            'md:w-auto md:shrink-0 md:self-center md:px-4 md:py-2 md:text-14 md:leading-[24px]'
                    )}
                />
            )}
        </InlineNotification>
    );
};
